/**
 * Which Settings sections this person sees, in this organisation.
 *
 * ---------------------------------------------------------------------------------------
 * WHY THIS IS A FUNCTION AND NOT JUST JSX
 * ---------------------------------------------------------------------------------------
 *
 * Somebody who belongs to several organisations saw the same Settings page in all of them. In a
 * supporter organisation that meant **Block Settings whose two dropdowns have zero options**,
 * Class code rules for an organisation with no classes, and Class defaults that would set the
 * absence limit across nothing.
 *
 * Nothing leaked — every resolver behind the page already scopes to the working organisation.
 * The page simply offered controls that cannot do anything here.
 *
 * Pulling the arithmetic out makes "org 6 sees four sections, org 1 sees nine" a cheap
 * assertion. The page is 400-odd lines of JSX over eight queries, so testing the rule through a
 * render would cost far more and prove less.
 *
 * ---------------------------------------------------------------------------------------
 * THE RULE: ASK THE DATA, NOT THE ORGANISATION'S TYPE
 * ---------------------------------------------------------------------------------------
 *
 * A section is shown when the thing it configures **exists here**, never when the organisation
 * is of some kind. `isSupportingOrganisation` is deliberately unused: it answers "do you support
 * somebody", which is independent of "do you run classes", and an organisation doing both would
 * lose controls it could use.
 *
 * Where the data test is available it is also the stronger statement — not "this is irrelevant"
 * but **this control cannot function**. Block Settings is the clearest case: its options come
 * from `blockTags`, so with none the form cannot be filled in and its Save can only write null.
 */

export type SectionKey =
    | 'SWITCH_ORGANISATION'
    | 'SUPPORTING_ORGANISATIONS'
    | 'ROLES'
    | 'CLASS_CODE_RULES'
    | 'CLASS_DEFAULTS'
    | 'ATTENDANCE_DIGEST'
    | 'STREAMS'
    | 'TIME_ZONE'
    | 'BLOCK_SETTINGS'
    | 'PUSH_NOTIFICATIONS';

export interface SettingsGateInputs {
    /** Admin of the working organisation — data administration, not authority over roles. */
    isAdmin: boolean;
    /** `ORGANISATION: MANAGE` — authority over who can do what. */
    canAdministerRoles: boolean;
    /** `CREATE_ORGANISATION`. One person holds it, and only in organisation 1. */
    canCreateOrganisation: boolean;
    /** The SkuRules page's **own** gate, so the button cannot outlive the page's answer. */
    canConfigureSkuRules: boolean;
    /** Server-answered: a live course, or tag groups set up to describe courses. */
    runsClasses: boolean;
    /** Any stream reachable at VIEW. True in a supporter org — every org gets a Leadership stream. */
    hasStreams: boolean;
    /** How many block tags exist. Zero means the Block Settings dropdowns have no options. */
    blockTagCount: number;
    /** More than one live membership — otherwise the switcher leads to a page with one option. */
    hasMultipleOrganisations: boolean;
}

export const settingsSections = (i: SettingsGateInputs): Set<SectionKey> => {
    const shown = new Set<SectionKey>();

    if (i.hasMultipleOrganisations) shown.add('SWITCH_ORGANISATION');
    if (i.canCreateOrganisation) shown.add('SUPPORTING_ORGANISATIONS');
    if (i.canAdministerRoles) shown.add('ROLES');

    // Both halves earn their place. `runsClasses` is the relevance test; `canConfigureSkuRules`
    // is the permission the destination itself checks — it redirects back to Settings when false,
    // so gating the button on anything else can produce a link that bounces.
    if (i.runsClasses && i.canConfigureSkuRules) shown.add('CLASS_CODE_RULES');

    // Sets the manager and absence limit across many classes at once. With no classes it is a
    // form that acts on nothing.
    if (i.isAdmin && i.runsClasses) shown.add('CLASS_DEFAULTS');

    // The attendance digest only ever reports classes, so with none there is nothing for the
    // preview to show and the button would always come back empty — the same relevance test
    // the other class sections use. `canAdministerRoles` rather than `isAdmin` because the
    // query behind it is gated on `ORGANISATION: MANAGE`, and a button whose query throws is
    // worse than no button.
    if (i.canAdministerRoles && i.runsClasses) shown.add('ATTENDANCE_DIGEST');

    if (i.hasStreams) shown.add('STREAMS');

    // Every organisation has an audience somewhere, so a time zone always means something.
    if (i.isAdmin) shown.add('TIME_ZONE');

    // Not `runsClasses`: the test is whether there is anything to *choose*. An organisation could
    // in principle run classes without a block tag group — it would get no options and a Save
    // that writes null, which is the thing being prevented.
    if (i.isAdmin && i.blockTagCount > 0) shown.add('BLOCK_SETTINGS');

    // Stays visible everywhere, including supporter organisations. It reaches this organisation's
    // own subscribed members, which is a real audience anywhere — and the recipient count already
    // says "no subscribed app users yet" honestly when there are none.
    if (i.isAdmin) shown.add('PUSH_NOTIFICATIONS');

    return shown;
};
